import { Troubleshoot } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { usePageContext } from "@ryde/redux";
import {
  rideInvestigationRideIdAtom,
  selectedLiveMapMapRidesSF,
  TOOLTIP_DELAY_BUTTONS,
} from "@ryde/shared";

import { RIDE_INVESTIGATION_E2E } from "./investigation.constants";

/** Live-map toolbar button that opens Deep Investigation for the single selected ride. */
export const InvestigationTriggerButton: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const currentRideId = useRecoilValue(rideInvestigationRideIdAtom);
  const setRideId = useSetRecoilState(rideInvestigationRideIdAtom);

  const singleRideId = useMemo(() => {
    const ids = mapRides
      .map(ride => ride.id)
      .filter((id): id is number => typeof id === "number" && id > 0);

    return ids.length === 1 ? ids[0] : undefined;
  }, [mapRides]);

  // A ride without GPS opens too — its history, stations and passengers are still worth reading; the
  // investigation itself tells the user there is no drive to replay.
  const isActive = currentRideId !== undefined;
  const disabled = !isActive && singleRideId === undefined;

  // The tooltip says why the button cannot be used before it says what it does, so the reason for a disabled
  // state is always the message the user gets.
  const tooltipKey = disabled
    ? "rideMap.investigation.selectOneRide"
    : "rideMap.investigation.buttonTooltip";

  // Toggle: an open investigation closes; otherwise open it for the single selected ride.
  const onClick = useEvent(() => {
    if (currentRideId !== undefined) {
      setRideId(undefined);

      return;
    }

    if (singleRideId !== undefined) {
      setRideId(singleRideId);
    }
  });

  return (
    <FramerTooltip
      content={t(tooltipKey)}
      mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
    >
      <span>
        <IconButton
          size="small"
          color={isActive ? "primary" : "info"}
          disabled={disabled}
          onClick={onClick}
          aria-label={t("rideMap.investigation.title")}
          data-e2e={RIDE_INVESTIGATION_E2E.triggerButton}
        >
          <Troubleshoot />
        </IconButton>
      </span>
    </FramerTooltip>
  );
};
